/**
 * Where an employee stands right now (#864): employed, upcoming (a period starts later, none covers now) or
 * former. Mirrors the server's `EmploymentStatus`, which rides the wire as these camelCase strings.
 */
export type EmploymentStatus = 'employed' | 'upcoming' | 'former';

/** The fields {@link employmentStatusOf} reads. Both `EmployeeDto` and `EmployeeNl` carry them. */
export interface EmploymentStatusSource {
  isEmployed: boolean;
  joined?: Date | string | null;
  left?: Date | string | null;
  employmentStatus?: EmploymentStatus | null;
  employmentStartsAt?: Date | string | null;
}

/**
 * The ONE place the web app decides employed / upcoming / former. The roster and the details page both call it,
 * so the two screens cannot drift apart again (#864: the details page only knew `isEmployed` and called a new
 * hire "Nicht mehr beschäftigt").
 *
 * The server's `employmentStatus` is the answer whenever it is present: it is computed over every employment
 * period, so it sees a rehire that is dated in the future. The fallback only covers a response from a server
 * that predates the field, and it can only recognise a first period that has not started yet.
 */
export function employmentStatusOf(employee: EmploymentStatusSource, now: Date = new Date()): EmploymentStatus {
  if (employee.employmentStatus) return employee.employmentStatus;
  if (employee.isEmployed) return 'employed';
  const joined = employee.joined ? new Date(employee.joined) : null;
  if (joined && joined > now && !employee.left) return 'upcoming';
  return 'former';
}

/**
 * When an upcoming employee starts, or null when they are not upcoming. Falls back to `joined` for the same
 * older-server case as {@link employmentStatusOf}.
 */
export function employmentStartOf(employee: EmploymentStatusSource, now: Date = new Date()): Date | null {
  if (employmentStatusOf(employee, now) !== 'upcoming') return null;
  const raw = employee.employmentStartsAt ?? employee.joined;
  return raw ? new Date(raw) : null;
}
